// Path: ./src/app/components/SessionExpiredDialog/index.tsx
import * as React from "react";
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from "@mui/material";
import { SignInResponse } from "@swagger/typescript-fetch-goflyer";
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { useTranslation } from "react-i18next";
import { GoFlyerAdminContext } from "../../../contextApi/context";
import { setUser } from "../../../contextApi/reducer";
import { auth } from "../../../firebaseConfig";
import { hasUnsavedChanges } from "../../../hooks/useUnsavedChanges";
import { messages } from "../../../locales/messages";
import { adminApiServiceRaw } from "../../../utils/api";
import { readApiError } from "../../../utils/apiError";
import { clearAuthToken, setAuthToken } from "../../../utils/authTokenStorage";
import { safeLocalStorageClear } from "../../../utils/localStorage";
import {
  finishReauth,
  isReauthPending,
  subscribeToReauth,
} from "../../../utils/sessionExpiry";

const BRAND_ORANGE = "#c2410c";

/**
 * Opens when the API says the login has expired (401). The user signs in
 * again right here instead of being bounced to the login page, so whatever
 * is on screen (e.g. unsaved coordinate edits) is kept, and the requests that
 * failed are retried with the new token.
 */
export const SessionExpiredDialog = () => {
  const { t } = useTranslation();
  const { state, dispatch } = React.useContext(GoFlyerAdminContext);
  const isPending = React.useSyncExternalStore(
    subscribeToReauth,
    isReauthPending,
  );
  const isLoggedIn = Boolean(state.user?.isLoggedIn);

  const [identifier, setIdentifier] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [error, setError] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [confirmSignOut, setConfirmSignOut] = React.useState(false);

  // Nobody is signed in on this screen (e.g. the login page), so there is no
  // session to resume. Let any waiting request give up.
  React.useEffect(() => {
    if (isPending && !isLoggedIn) {
      finishReauth(null);
    }
  }, [isPending, isLoggedIn]);

  const resetForm = () => {
    setIdentifier("");
    setPassword("");
    setError("");
    setConfirmSignOut(false);
  };

  const resumeSession = (response: SignInResponse) => {
    // The page on screen belongs to the current user. Carrying on as someone
    // else would mix two accounts' data, so only accept the same user.
    const currentUserId = state.gfAdminUser?.id ?? state.user?.id;
    if (currentUserId && response.id !== currentUserId) {
      setError(t(messages.Session_Expired_Different_Account()));
      return;
    }
    setAuthToken(response.token);
    if (state.user) {
      dispatch(
        setUser({
          ...state.user,
          token: response.token,
          jwtToken: response.token,
        }),
      );
    }
    resetForm();
    finishReauth(response.token);
  };

  const signIn = async (request: () => Promise<SignInResponse>) => {
    setIsSubmitting(true);
    setError("");
    try {
      const response = await request();
      if (response?.token) {
        resumeSession(response);
      } else {
        setError(
          response?.message || t(messages.Session_Expired_Sign_In_Failed()),
        );
      }
    } catch (signInError) {
      const { message } = await readApiError(signInError);
      setError(message || t(messages.Session_Expired_Sign_In_Failed()));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePasswordSignIn = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!identifier.trim() || !password) {
      setError(t(messages.Session_Expired_Fields_Required()));
      return;
    }
    signIn(() =>
      adminApiServiceRaw.gfAdminUserAdminControllerSignIn({
        identifier: identifier.trim(),
        password,
      }),
    );
  };

  const handleGoogleSignIn = () =>
    signIn(async () => {
      const result = await signInWithPopup(auth, new GoogleAuthProvider());
      const idToken = await result.user.getIdToken();
      return adminApiServiceRaw.gfAdminUserAdminControllerFirebaseLoginOrSignUp(
        { idToken },
      );
    });

  const handleSignOut = () => {
    if (hasUnsavedChanges() && !confirmSignOut) {
      setConfirmSignOut(true);
      return;
    }
    resetForm();
    clearAuthToken();
    safeLocalStorageClear();
    dispatch(setUser(undefined));
    finishReauth(null);
  };

  return (
    <Dialog
      open={isPending && isLoggedIn}
      disableEscapeKeyDown
      // Above Layout's full-screen loader (zIndex 999999), which is often
      // still up while the request that hit the 401 is waiting on us.
      sx={{ zIndex: 1000000 }}
      maxWidth="xs"
      fullWidth
      aria-labelledby="session-expired-title"
      aria-describedby="session-expired-description"
    >
      <form onSubmit={handlePasswordSignIn} noValidate>
        <DialogTitle id="session-expired-title">
          {t(messages.Session_Expired_Title())}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="session-expired-description" sx={{ mb: 2 }}>
            {t(messages.Session_Expired_Description())}
          </DialogContentText>
          <Stack spacing={2}>
            <TextField
              autoFocus
              label={t(messages.Session_Expired_Identifier_Label())}
              autoComplete="username"
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              disabled={isSubmitting}
              fullWidth
            />
            <TextField
              label={t(messages.Password())}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              disabled={isSubmitting}
              fullWidth
            />
            {error && (
              <Alert severity="error" role="alert">
                {error}
              </Alert>
            )}
            {confirmSignOut && (
              <Alert severity="warning" role="alert">
                {t(messages.Session_Expired_Unsaved_Warning())}
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: "wrap" }}>
          <Button
            onClick={handleSignOut}
            disabled={isSubmitting}
            color={confirmSignOut ? "error" : "inherit"}
            sx={{ mr: "auto", textTransform: "none" }}
          >
            {confirmSignOut
              ? t(messages.Session_Expired_Sign_Out_Anyway())
              : t(messages.Session_Expired_Sign_Out())}
          </Button>
          <Button
            onClick={handleGoogleSignIn}
            disabled={isSubmitting}
            variant="outlined"
            sx={{
              textTransform: "none",
              color: BRAND_ORANGE,
              borderColor: BRAND_ORANGE,
            }}
          >
            {t(messages.Session_Expired_Google())}
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting}
            variant="contained"
            sx={{
              textTransform: "none",
              bgcolor: BRAND_ORANGE,
              "&:hover": { bgcolor: "#9a3412" },
            }}
          >
            {t(messages.Sign_In())}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
